/**
 * News archive template
 * This file contains logic specific to a template.
 * It is intended to ONLY be loaded on templates that require
 * this specific functionality.
 **/

import Global from './lib/global';
import Logger from './lib/logger';

// Vars
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

const NAME = 'News archive';

let page = 1;
let limit = 9; // 9 posts per page (featured post is separate)
let offset = 1; // Start at offset 1 to skip the featured post

let continueFetching = true;
const themePostGridDom = document.querySelector('[data-theme-post-grid]');
const postType = themePostGridDom ? themePostGridDom.getAttribute('data-theme-post-grid-post-type') || 'news' : 'news';

// Get base URL for pushState updates
const getBaseUrl = () => {
	const url = new URL(window.location.href);
	// Remove existing page parameter if present
	url.searchParams.delete('page');
	return url.toString().split('?')[0];
};
const baseUrl = getBaseUrl();

// Logic
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

/**
 * init - Initialize the page
 **/
const init = () => {
	Logger.log(`${NAME} > loaded`);

	// Handle browser back/forward buttons
	if (window.history && window.history.pushState) {
		window.addEventListener('popstate', (event) => {
			if (event.state && event.state.page) {
				// User navigated back/forward - would need to restore scroll position
				// For now, just log it. Full implementation would require saving/restoring state
				Logger.log(`${NAME} > popstate to page ${event.state.page}`);
			}
		});
	}

	initInfiniteScroll();
}

/**
 * initInfiniteScroll - Initialize the infinite scroll
 * @returns {void}
 */
const initInfiniteScroll = async () => {
	Logger.log(`${NAME} > initInfiniteScroll`);

	if (!themePostGridDom) return;

	const sentinel = themePostGridDom.querySelector('[data-theme-post-grid-sentinel]');

	// Set up intersection observer for the bottom of the post grid to be showing
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				if (!continueFetching) return;
				// Fetch the posts
				fetchPosts().then(() => {
					// Re-observe the bottom after new posts are added
					if (continueFetching) {
						observer.observe(sentinel);
					}
				});
				observer.unobserve(entry.target);
			}
		});
	}, {
		// Trigger when the bottom edge is 200px above the viewport bottom
		rootMargin: '0px 0px 200px 0px',
		threshold: 0
	});

	// Wait for the posts container to be loaded, then observe it
	setTimeout(() => {
		observer.observe(sentinel, { childList: true, subtree: true });
	}, 500);
}

/**
 * fetchPosts - Fetch the posts
 * @returns {Promise} - The promise
 */
const fetchPosts = async () => {
	Logger.log(`${NAME} > fetchPosts`);

	// Increment the page
	page++;
	// Calculate offset: 1 (for featured post) + (page - 1) * limit
	offset = 1 + (page - 1) * limit;

	const formData = new FormData();

	formData.append('action', 'wonder_get_post_grid_items');
	formData.append('nonce', window.global_vars.ajax_nonce);
	formData.append('page', page);
	formData.append('limit', limit);
	formData.append('offset', offset);
	formData.append('post_type', postType);

	const url = `${window.global_vars.ajax_url}`;

	// Fetch from wp-admin
	const response = await fetch(url, {
		method: 'POST',
		body: formData,
	});
	const data = await response.json();

	// If there are no more posts, set continueFetching to false
	if (data.post_grid_items.length === 0) {
		continueFetching = false;
	}

	// Append the posts to the post grid
	appendPosts(data.post_grid_items);

	// Update URL with pushState for SEO and UX (per Google's recommendations)
	if (window.history && window.history.pushState) {
		const newUrl = page > 1 ? `${baseUrl}?page=${page}` : baseUrl;
		window.history.pushState({ page: page }, '', newUrl);
	}

	return data;
}

/**
 * appendPosts - Append the posts to the post grid
 * @param {Array} posts - The posts to append
 * @returns {void}
 */
const appendPosts = (posts) => {
	Logger.log(`${NAME} > appendPosts`);
	const themePostGridPostsDom = themePostGridDom.querySelector('.theme-post-grid__posts');

	// Loop through each post, wrap in <div class="theme-post-grid__post">, and append to the post grid
	// Use insertAdjacentHTML instead of innerHTML += to avoid re-rendering existing content
	posts.forEach((post) => {
		themePostGridPostsDom.insertAdjacentHTML('beforeend', `<div class="theme-post-grid__post">${post}</div>`);
	});
}

// Bootstrap global functionality
Global();

// Bootstrap page-specific functionality
init();

// Do not edit below this line...
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
Logger.log(`${NAME} > loaded`);
